import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminTable } from "../../components/admin/AdminTable";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { Input } from "../../components/ui/Input";
import { useUsers } from "../../hooks/useUsers";
import { formatDate, formatDateTime, statusTone, titleCase } from "../../lib/format";
import { userService } from "../../services/userService";

export function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const { data: users, loading, error, refresh } = useUsers(search);
  const [actionError, setActionError] = useState<string | null>(null);

  async function setUserStatus(userId: string, status: "active" | "disabled") {
    setActionError(null);
    try {
      await userService.updateUserStatus(userId, status);
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to update user.");
    }
  }

  async function deleteTestUser(userId: string, email: string) {
    setActionError(null);
    const confirmation = window.prompt(
      `Type ${email} to delete this test user and their auth account.`,
    );

    if (!confirmation) {
      return;
    }

    try {
      await userService.deleteTestUser(userId, confirmation);
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to delete user.");
    }
  }

  return (
    <div>
      <AdminPageHeader title="Users" description="Review accounts, memberships, roles and account status." />
      <div className="mb-5 max-w-md">
        <Input label="Search users" placeholder="Name or email" value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>
      {actionError ? <div className="mb-5 rounded-md border border-red-900/20 bg-red-50 p-4 text-sm text-red-900">{actionError}</div> : null}
      {loading ? <FullPageLoader label="Loading users" /> : null}
      {error ? <EmptyState title="Unable to load users.">{error}</EmptyState> : null}
      {!loading && !error ? (
        users?.length ? (
          <AdminTable headers={["Name", "Email", "Status", "Roles", "Workspace plans", "Created", "Last login", "Actions"]}>
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-4 py-3 font-medium text-ink">{user.full_name || "Unnamed"}</td>
                <td className="px-4 py-3">{user.email}</td>
                <td className="px-4 py-3"><Badge tone={statusTone(user.status)}>{user.status}</Badge></td>
                <td className="px-4 py-3">{user.memberships?.map((item) => titleCase(item.role)).join(", ") || "None"}</td>
                <td className="px-4 py-3">
                  {user.memberships?.length ? user.memberships.map((membership) => (
                    <div className="mb-2 last:mb-0" key={membership.id}>
                      {membership.tenants ? (
                        <Link className="link" to={`/admin/tenants/${membership.tenants.id}`}>
                          {membership.tenants.name}: {membership.tenants.plan}
                        </Link>
                      ) : (
                        <span>Platform access</span>
                      )}
                    </div>
                  )) : "None"}
                </td>
                <td className="px-4 py-3">{formatDate(user.created_at)}</td>
                <td className="px-4 py-3">{formatDateTime(user.last_login_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    {user.status === "active" ? (
                      <Button variant="secondary" onClick={() => void setUserStatus(user.id, "disabled")}>Disable</Button>
                    ) : (
                      <Button variant="secondary" onClick={() => void setUserStatus(user.id, "active")}>Reactivate</Button>
                    )}
                    <Button variant="danger" onClick={() => void deleteTestUser(user.id, user.email)}>Delete test user</Button>
                  </div>
                </td>
              </tr>
            ))}
          </AdminTable>
        ) : (
          <EmptyState title="No users found." />
        )
      ) : null}
    </div>
  );
}
